<template>
  <SettingsPageFull>
    <template #title>
      <div class="flex space-x-3">
        <font-awesome-icon :icon="['fal', 'landmark']" size="2x" />
        <span class="text-2xl font-bold capitalize-first">
          {{
            this.$route.name === 'settings_create_company_route'
              ? $t('button.add_new_f', { var: $t('global.company') })
              : pageTitle
          }}
        </span>
      </div>
    </template>
    <template #cta>
      <div class="flex gap-x-3">
        <div v-if="editionMode && this.$can('manage_content')">
          <BaseButton
            :isElevated="true"
            icon="paper-plane"
            @click="submitForm"
            :title="$t(buttonText, { var: $t('global.company') })"
          ></BaseButton>
        </div>
        <BaseButton
          v-if="
            this.$route.name !== 'settings_create_company_route' &&
            $can('manage_content')
          "
          color="warning"
          :icon="editionMode ? 'times' : 'pen'"
          @click="getCompanyAndChangeEditionMode"
          :title="editionMode ? $t('button.cancel') : $t('button.edit')"
        />
        <BaseButton
          color="dark"
          icon="long-arrow-left"
          @click="this.$router.push({ name: 'settings_list_company_route' })"
          :title="$t('button.back')"
        />
      </div>
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <PageSection
          disable-sticky-margin
          :title="$t('global.general_information')"
        >
          <div class="grid-form">
            <BaseShowEditInput
              :label="$t('attributes.name')"
              v-model="company.name"
              :errors="errors.name"
              :placeholder="$t('attributes.name')"
              :editionMode="editionMode"
              :required="true"
            />
            <BaseShowEditInput
              :label="$t('attributes.TVA')"
              v-model="company.TVA"
              :errors="errors.TVA"
              :placeholder="$t('attributes.TVA')"
              :editionMode="editionMode"
            />
            <BaseShowEditInput
              :label="businessNumberLabel"
              v-model="company.business_number"
              :errors="errors.business_number"
              :placeholder="businessNumberLabel"
              :editionMode="editionMode"
              :required="company.country === 'FR'"
            />
            <BaseShowEditInput
              :label="$t('attributes.email')"
              v-model="company.email"
              :errors="errors.email"
              :placeholder="$t('attributes.email')"
              :editionMode="editionMode"
              :required="true"
            />
            <BaseShowEditInput
              :label="$t('attributes.bank')"
              v-model="company.bank"
              :errors="errors.bank"
              :placeholder="$t('attributes.bank')"
              :editionMode="editionMode"
            />
            <BaseShowEditPhoneInput
              :label="$t('attributes.phone')"
              :editionMode="editionMode"
              name="phone"
              v-model:countryValue="company.phone_country"
              v-model:numberValue="company.phone_field"
              :errors="errors"
              @change.self="getInputValue"
              :metadata="metadata"
            />
            <BaseShowEditInput
              :label="$t('attributes.website')"
              v-model="company.website"
              :errors="errors.website"
              :placeholder="$t('attributes.website')"
              :editionMode="editionMode"
            />
            <BaseLanguageShowEditSelectSimple
              v-model="company.language"
              :metadata="metadata"
              :errors="errors"
              :edition-mode="editionMode"
            />
          </div>
        </PageSection>
        <PageSection disable-sticky-margin :title="$t('attributes.address')">
          <div class="grid-form">
            <div class="md:col-span-2">
              <BaseShowEditInput
                :label="$t('attributes.street')"
                v-model="company.street"
                :errors="errors.street"
                :placeholder="$t('attributes.street')"
                :editionMode="editionMode"
              />
            </div>
            <BaseShowEditInput
              :label="$t('attributes.post_code')"
              v-model="company.post_code"
              :errors="errors.post_code"
              :placeholder="$t('attributes.post_code')"
              :editionMode="editionMode"
            />
            <BaseShowEditInput
              :label="$t('attributes.city')"
              v-model="company.city"
              :errors="errors.city"
              :placeholder="$t('attributes.city')"
              :editionMode="editionMode"
            />
            <BaseCountryShowEditSelectSimple
              v-model="company.country"
              :metadata="metadata"
              :errors="errors"
              :edition-mode="editionMode"
            />
          </div>
        </PageSection>
        <PageSection
          v-if="company?.id && $route.name !== 'settings_create_company_route'"
          disable-sticky-margin
          title="Peppol"
        >
          <PeppolCompanyComponent :company="company" />
        </PageSection>
      </div>
      <!--      <div class='flex justify-end mr-10 pb-5' v-if="editionMode && this.$can('manage_content')">-->
      <!--        <BaseButton :isElevated='true'-->
      <!--                    icon='paper-plane'-->
      <!--                    @click='submitForm'-->
      <!--                    :title="$t(buttonText, { var: $t('global.company')} ) ">-->
      <!--        </BaseButton>-->
      <!--      </div>-->
    </template>
  </SettingsPageFull>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'

import PeppolCompanyComponent from '@c/Partner/Peppol/PeppolCompanyComponent.vue'

export default {
  name: 'TheCompanyFormPage',
  components: { PeppolCompanyComponent },
  data() {
    return {
      editionMode: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/getSelectedCompany',
      errors: 'auth/getErrors',
      metadata: 'company/getCompanyMetadata'
    }),
    buttonText() {
      return this.$route.name === 'settings_create_company_route'
        ? 'button.add_new_f'
        : 'button.edit_new_f'
    },
    // One field, named after the company's country (SIREN in France).
    businessNumberLabel() {
      return this.company?.country === 'FR'
        ? this.$t('attributes.siren')
        : this.$t('attributes.business_number')
    },
    pageTitle() {
      return this.company ? this.company.name : ''
    }
  },
  methods: {
    ...mapActions({
      fetchCompanyByID: 'company/fetchCompanyByID',
      fetchNewCompany: 'company/fetchNewCompany',
      postNewCompany: 'company/storeNewCompany',
      putUpdateCompany: 'company/updateCompany'
    }),
    getCompanyAndChangeEditionMode() {
      if (this.editionMode) {
        this.fetchCompanyByID(this.$route.params.id)
      }
      this.editionMode = !this.editionMode
    },
    async submitForm() {
      if (this.$route.name === 'settings_create_company_route') {
        await this.postNewCompany({
          company: this.company
        }).then(() => {
          this.$h.toastSuccess(
            this.$t('toasts.created_f', { var: this.$t('global.company') })
          )
          this.$router.push({ name: 'settings_list_company_route' })
        })
      } else {
        await this.putUpdateCompany({
          id: this.$route.params.id,
          company: this.company
        }).then(() => {
          this.$h.toastSuccess(
            this.$t('toasts.updated_f', { var: this.$t('global.company') })
          )
          this.editionMode = false
        })
      }
    }
  },
  async beforeMount() {
    if (this.$route.name !== 'settings_create_company_route') {
      await this.fetchCompanyByID(this.$route.params.id)
    } else {
      await this.fetchNewCompany()
      this.editionMode = true
    }
  }
}
</script>
